import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Video,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  ExternalLink,
  Copy,
} from 'lucide-react';
import { useToastContext } from '../../contexts/ToastContext';
import {
  getConsultations,
  acceptConsultation,
  rejectConsultation,
  completeConsultation,
} from '../../services/consultationService';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';

const STATUS_TABS = [
  { key: 'pending', label: 'Pending', activeClass: 'border-amber-600 text-amber-600' },
  { key: 'accepted', label: 'Accepted', activeClass: 'border-green-600 text-green-600' },
  { key: 'completed', label: 'Completed', activeClass: 'border-blue-600 text-blue-600' },
  { key: 'rejected', label: 'Rejected', activeClass: 'border-red-600 text-red-600' },
  { key: '', label: 'All', activeClass: 'border-black text-black' },
];

const normalizeMeetingLink = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, '')}`;
};

const getApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (data?.message) return data.message;
  const errors = data?.errors;
  if (errors && typeof errors === 'object') {
    const first = Object.values(errors).flat().find(Boolean);
    if (first) return String(first);
  }
  return fallback;
};

const VendorConsultations = () => {
  const { toast } = useToastContext();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [acceptingId, setAcceptingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [showAcceptModal, setShowAcceptModal] = useState(null);
  const [acceptedMeetingLink, setAcceptedMeetingLink] = useState(null);
  const [meetingLink, setMeetingLink] = useState('');
  const [meetingLinkError, setMeetingLinkError] = useState('');
  const [vendorNotes, setVendorNotes] = useState('');

  const params = statusFilter ? { status: statusFilter } : {};
  const { data: consultationsData, isLoading } = useQuery({
    queryKey: ['vendorConsultations', params],
    queryFn: () => getConsultations(params),
  });

  const consultations = consultationsData?.data?.data || consultationsData?.data || [];

  const acceptMutation = useMutation({
    mutationFn: ({ id, data }) => acceptConsultation(id, data),
    onSuccess: (res) => {
      const link = res?.data?.google_meet_link;
      if (link) {
        setAcceptedMeetingLink(link);
      } else {
        toast.success(null, 'Consultation accepted. The meeting link will be sent to the client.');
        closeAcceptModal();
      }
      queryClient.invalidateQueries({ queryKey: ['vendorConsultations'] });
    },
    onError: (err) => {
      const message = getApiErrorMessage(err, 'Failed to accept consultation');
      const linkError = err?.response?.data?.errors?.google_meet_link?.[0];
      if (linkError) {
        setMeetingLinkError(linkError);
      }
      toast.error('Could not accept consultation', message);
    },
    onSettled: () => setAcceptingId(null),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, data }) => rejectConsultation(id, data),
    onSuccess: () => {
      toast.success(null, 'Consultation rejected');
      queryClient.invalidateQueries({ queryKey: ['vendorConsultations'] });
    },
    onError: (err) => {
      toast.error(null, err?.response?.data?.message || 'Failed to reject consultation');
    },
    onSettled: () => setRejectingId(null),
  });

  const completeMutation = useMutation({
    mutationFn: (id) => completeConsultation(id),
    onSuccess: async () => {
      toast.success(null, 'Consultation marked as completed');
      await queryClient.refetchQueries({ queryKey: ['vendorConsultations'] });
    },
    onError: (err) => {
      toast.error(null, err?.response?.data?.message || 'Failed to complete consultation');
    },
    onSettled: () => setCompletingId(null),
  });

  const handleAccept = (consultation) => {
    const normalizedLink = normalizeMeetingLink(meetingLink);
    if (!normalizedLink) {
      setMeetingLinkError('Please enter a meeting link (Zoom, Google Meet, Teams, etc.)');
      toast.error('Meeting link required', 'Add a full link starting with https://');
      return;
    }

    try {
      // eslint-disable-next-line no-new
      new URL(normalizedLink);
    } catch {
      setMeetingLinkError('Enter a valid URL (e.g. https://zoom.us/j/123 or https://meet.google.com/abc-defg-hij)');
      toast.error('Invalid meeting link', 'Use a full https:// link for Zoom, Meet, Teams, or similar.');
      return;
    }

    setMeetingLinkError('');
    setAcceptingId(consultation.id);
    acceptMutation.mutate({
      id: consultation.id,
      data: {
        google_meet_link: normalizedLink,
        vendor_notes: vendorNotes || null,
      },
    });
  };

  const closeAcceptModal = () => {
    setShowAcceptModal(null);
    setAcceptedMeetingLink(null);
    setMeetingLink('');
    setMeetingLinkError('');
    setVendorNotes('');
  };

  const handleCopyLink = () => {
    if (acceptedMeetingLink) {
      navigator.clipboard.writeText(acceptedMeetingLink);
      toast.success(null, 'Link copied to clipboard');
    }
  };

  const handleComplete = (consultation) => {
    if (!window.confirm('Mark this consultation as completed?')) return;
    setCompletingId(consultation.id);
    completeMutation.mutate(consultation.id);
  };

  const handleReject = (consultation) => {
    if (!window.confirm('Are you sure you want to reject this consultation request?')) return;
    setRejectingId(consultation.id);
    rejectMutation.mutate({ id: consultation.id, data: {} });
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD');
  const formatTime = (t) => {
    if (!t) return 'TBD';
    const parts = String(t).split(':');
    const h = parseInt(parts[0] || 0, 10);
    const m = parts[1] || '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${h12}:${m} ${ampm}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 sm:p-6">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Consultations</h1>
          <p className="text-gray-600 mt-1">View and manage consultation requests from clients</p>
        </div>

        <div className="bg-white rounded-t-lg shadow-sm border border-gray-200 border-b-0 px-4 pt-4">
          <nav className="flex flex-wrap gap-1" aria-label="Consultation status tabs">
            {STATUS_TABS.map((tab) => {
              const isActive = statusFilter === tab.key;
              return (
                <button
                  key={tab.key || 'all'}
                  type="button"
                  onClick={() => setStatusFilter(tab.key)}
                  className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors border-b-2 -mb-px ${
                    isActive
                      ? `${tab.activeClass} bg-white`
                      : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="bg-white rounded-b-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading consultations...</p>
            </div>
          ) : consultations.length === 0 ? (
            <div className="p-12 text-center">
              <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Consultations</h3>
              <p className="text-gray-600">
                {statusFilter
                  ? `No ${statusFilter} consultations.`
                  : 'Consultation requests from clients will appear here when they use the "Book Consultation" button on your service pages.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {consultations.map((c) => (
                <div key={c.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-medium text-gray-900">
                          {c.client?.name || c.client_name || c.client_display_name || 'Guest Client'}
                        </h3>
                        {!c.client && !c.client_id && (
                          <span className="px-2 py-0.5 text-[11px] rounded-full bg-slate-100 text-slate-700 font-medium border border-slate-200">
                            Guest
                          </span>
                        )}
                        {getStatusBadge(c.status)}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(c.scheduled_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatTime(c.scheduled_time)}
                        </span>
                        {c.service?.service_name && (
                          <span className="text-gray-700 font-medium">{c.service.service_name}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-1.5">
                        {(c.client?.email || c.client_email || c.client_display_email) && (
                          <span>Email: {c.client?.email || c.client_email || c.client_display_email}</span>
                        )}
                        {(c.client?.phone || c.client_phone || c.client_display_phone) && (
                          <span>Phone: {c.client?.phone || c.client_phone || c.client_display_phone}</span>
                        )}
                      </div>
                      {c.questionnaire_responses && Object.keys(c.questionnaire_responses).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-sm text-indigo-600 cursor-pointer hover:underline">
                            View client responses
                          </summary>
                          <ul className="mt-2 pl-4 text-sm text-gray-600 space-y-1 bg-gray-50/70 p-2.5 rounded-lg border border-gray-100">
                            {Object.entries(c.questionnaire_responses).map(([k, v]) =>
                              v ? (
                                <li key={k}>
                                  <span className="font-medium text-gray-700">{k}:</span> {v}
                                </li>
                              ) : null
                            )}
                          </ul>
                        </details>
                      )}
                    </div>
                    {c.status === 'pending' && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => setShowAcceptModal(c)}
                          disabled={acceptingId || rejectingId}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(c)}
                          disabled={acceptingId || rejectingId}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                    {c.status === 'accepted' && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {c.google_meet_link && (
                          <a
                            href={c.google_meet_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Join meeting
                          </a>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleComplete(c)}
                          disabled={completingId || acceptingId || rejectingId}
                          loading={completeMutation.isPending && completingId === c.id}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Mark Completed
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Accept Modal */}
      {showAcceptModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={closeAcceptModal} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              {acceptedMeetingLink ? (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Consultation Accepted</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    The client will receive this meeting link by email. You can also share it directly.
                  </p>
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 mb-4">
                    <a
                      href={acceptedMeetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-sm text-indigo-600 hover:underline truncate"
                    >
                      {acceptedMeetingLink}
                    </a>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCopyLink}
                      title="Copy link"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button variant="primary" className="w-full" onClick={closeAcceptModal}>
                    Done
                  </Button>
                </>
              ) : (
                <>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Accept Consultation</h3>
              <p className="text-sm text-gray-600 mb-4">
                Add your video call link (Zoom, Google Meet, Microsoft Teams, etc.). The client will receive it by email.
              </p>
              <div className="space-y-4">
                <Input
                  label="Meeting link"
                  type="url"
                  placeholder="https://zoom.us/j/... or meet.google.com/..."
                  value={meetingLink}
                  onChange={(e) => {
                    setMeetingLink(e.target.value);
                    if (meetingLinkError) setMeetingLinkError('');
                  }}
                  error={meetingLinkError}
                />
                <p className="text-xs text-gray-500 -mt-2">
                  Paste a full link with https://, or enter zoom.us/... and we&apos;ll add https:// for you.
                </p>
                <Input
                  label="Notes (optional)"
                  placeholder="Any notes for the client"
                  value={vendorNotes}
                  onChange={(e) => setVendorNotes(e.target.value)}
                />
              </div>
              <div className="flex gap-2 mt-6 justify-end">
                <Button variant="outline" onClick={closeAcceptModal}>
                  Cancel
                </Button>
                <Button
                  onClick={() => handleAccept(showAcceptModal)}
                  loading={acceptMutation.isPending}
                  disabled={!meetingLink.trim() || acceptMutation.isPending}
                >
                  Accept & Send Link
                </Button>
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorConsultations;
